/**
 * List row selection, keyed by id and holding the row itself so totals and
 * bulk actions keep working after the user pages away from a selected row.
 */
export type SelectableRow = { id?: string | null };
export type RowSelection<T> = ReadonlyMap<string, T>;

/**
 * Toggle the row at `index`. With `shiftKey` and an anchor on this page, every
 * row between the anchor and `index` takes the clicked row's new state.
 */
export function toggleRow<T extends SelectableRow>(
  selection: RowSelection<T>,
  rows: T[],
  index: number,
  anchor: number | null,
  shiftKey: boolean,
): Map<string, T> {
  const next = new Map(selection);
  const target = rows[index];
  if (!target?.id) return next;

  const willSelect = !selection.has(target.id);
  const useRange = shiftKey && anchor != null && anchor >= 0 && anchor < rows.length;
  const from = useRange ? Math.min(anchor, index) : index;
  const to = useRange ? Math.max(anchor, index) : index;

  for (let i = from; i <= to; i += 1) {
    const row = rows[i];
    if (!row?.id) continue;
    if (willSelect) next.set(row.id, row);
    else next.delete(row.id);
  }
  return next;
}

export function pageSelectionState<T extends SelectableRow>(selection: RowSelection<T>, rows: T[]) {
  const ids = rows.map((r) => r?.id).filter(Boolean) as string[];
  const all = ids.length > 0 && ids.every((id) => selection.has(id));
  const some = ids.some((id) => selection.has(id));
  return { all, some };
}

/** Header checkbox: clear this page when it is fully selected, otherwise add all of it. */
export function togglePage<T extends SelectableRow>(selection: RowSelection<T>, rows: T[]): Map<string, T> {
  const next = new Map(selection);
  const { all } = pageSelectionState(selection, rows);
  for (const row of rows) {
    if (!row?.id) continue;
    if (all) next.delete(row.id);
    else next.set(row.id, row);
  }
  return next;
}

export function addRows<T extends SelectableRow>(selection: RowSelection<T>, rows: T[]): Map<string, T> {
  const next = new Map(selection);
  for (const row of rows) {
    if (row?.id) next.set(row.id, row);
  }
  return next;
}
